import {
  Component,
  ViewChild,
  ElementRef,
  ComponentFactoryResolver,
  Injector,
  ApplicationRef,
  EmbeddedViewRef,
  ComponentRef,
  OnDestroy,
} from '@angular/core';
import { GeneralStatusSummaryComponent } from '../general-status-summary/general-status-summary.component';
import { IGeneralSummaryStatus } from 'src/app/modules/widget/interfaces/general-summary-status';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetBasicComponent } from '../../../dashboard-widget-basic/dashboard-widget-basic.component';
import { ParameterHandlerService, CacheService } from 'projects/common/src/app/services';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { IGeneralSummaryStatusWidget } from 'src/app/modules/widget/interfaces/general-summary-status-widget';
import { IMachineStatusSummary } from 'src/app/modules/widget/interfaces/machines-status-summary';
import { IShift } from 'src/app/modules/core/interfaces';
import { IUser } from 'projects/common/src/app/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';

@Component({
  selector: 'app-general-status-summary-group',
  templateUrl: './general-status-summary-group.component.html',
  styleUrls: ['./general-status-summary-group.component.scss'],
})
export class GeneralStatusSummaryGroupComponent extends DashboardWidgetBasicComponent implements OnDestroy {
  @ViewChild('statusSummary', { read: ElementRef, static: false })
  public generalStatusSummaryView: ElementRef;

  public statusCollection: IGeneralSummaryStatus[];
  public machineCollection: IMachineStatusSummary[];
  public machineNameList: string[];
  public shortMachineList: string[];
  public generalStatusSummaryComponents: ComponentRef<any>[];
  public title: string;
  private machineType: string;
  public isViewMorePressed: boolean;
  public displayMessage = false;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private datePipe: DatePresentationPipe,
    private languageService: LanguageService,
    protected screenViewService: ScreenViewService,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.statusCollection = [];
    this.machineNameList = [];
    this.generalStatusSummaryComponents = [];
    this.shortMachineList = [];
    this.isViewMorePressed = false;
    this.viewMore(true);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * build view to reload when date is changed
   *
   * @param values default value
   */
  public buildView(values: any): void {
    super.buildView(values);
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value as IUser;
    this.title = `${this.languageService.message('company')}: ${user.company.name},
                  ${this.languageService.message('machine_name')}: ${this.languageService.message(machineType.name)},
                  ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
                  ${this.languageService.message('shift_', '')}: ${shift.name}`;
    this.machineType = machineType.name;
    this.initStatusCollection(values);
  }

  /**
   * Function to load status
   */
  public loadComponentData(statusCollection?: IGeneralSummaryStatus[]): void {
    this.destroyAllComponents();
    if (!statusCollection) {
      statusCollection = this.statusCollection;
    }

    let hasSeparator = false;

    for (const summary of statusCollection) {
      const componentRef = this.componentFactoryResolver.resolveComponentFactory(GeneralStatusSummaryComponent).create(this.injector);

      componentRef.instance.machineType = this.machineType;
      componentRef.instance.hasSeparator = hasSeparator;
      componentRef.instance.summaryStatus = summary;

      this.appRef.attachView(componentRef.hostView);
      const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      this.generalStatusSummaryView.nativeElement.appendChild(domElem);
      this.generalStatusSummaryComponents.push(componentRef);
      hasSeparator = true;
    }
  }

  /**
   * load machine list
   */
  public loadMachineComponentData(): void {
    this.machineNameList = [];
    for (const machine of this.machineCollection) {
      this.machineNameList.push(machine.machineName);
    }

    this.cutList();
  }

  /**
   * Function to clear the machineComponents array and destroy its components
   */
  public destroyAllComponents(): void {
    if (this.generalStatusSummaryComponents) {
      for (const component of this.generalStatusSummaryComponents) {
        component.destroy();
      }
    }

    this.generalStatusSummaryComponents = [];
  }

  /**
   * initializes data statusCollection to build cards
   */
  public initStatusCollection(summary: IGeneralSummaryStatusWidget): void {
    this.statusCollection = summary.statusLog;
    this.machineCollection = summary.statusMachine;
    this.loadComponentData();
    this.loadMachineComponentData();
    this.setDisplayMessage();
  }

  /**
   * Determines if the view more button is pressed or not
   *
   * @param flag boolean parameter to determine if it is the 1st time calling the method
   */
  public viewMore(flag?: boolean): void {
    if (!flag) {
      this.isViewMorePressed = !this.isViewMorePressed;
    }
  }

  /**
   * Generates a shortened list if the original list of machines is larger than the determined limit
   */
  public cutList(): void {
    this.shortMachineList = [];

    if (this.machineNameList.length > 20) {
      for (let i = 0; i < 20; i++) {
        this.shortMachineList[i] = this.machineNameList[i];
      }
    }
  }

  /**
   * Display no data message
   */
  private setDisplayMessage(): void {
    if (!this.statusCollection.length || !this.machineCollection.length) {
      this.displayMessage = true;
    } else {
      this.displayMessage = false;
    }
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
